import { useConfig, useValidation, type Rules } from '@seek/libs-shared';
import { Heading, Stack } from 'braid-design-system';
import { useEffect } from 'react';
import {
  CURRENT_PAGE_PERKS_AND_BENEFITS,
  trackContinuePressed,
  trackDisplayPage,
  trackSkipQuestionPressed,
} from '../../../shared/tracking/writeAReview/trackingEvents';
import { ReviewPageLayoutNoStepper } from '../../components/ReviewPageLayout/ReviewPageLayoutNoStepper';
import type { Rating } from '../../components/StarSelector/StarSelector';
import { PerksAndBenefitsRatingQuestion } from '../../questions/RatingQuesions/PerksAndBenefitsRatingQuestion';
import { PerksAndBenefitsDetailsQuestion } from '../../questions/SimpleQuestions/PerksAndBenefitsDetailsQuestion';
import {
  ExperimentTwoProgress,
  type PageProps,
  type ReviewPayload,
} from '../../type';
import { validationRules } from '../../validation/validationRules';

type Props = PageProps & {
  isSubmitting: boolean;
};

export const PerksAndBenefitsPage = ({
  review,
  reviewFormContext,
  setPage,
  setter,
  pageNumber,
  isSubmitting,
  onSubmit,
}: Props) => {
  const config = useConfig();

  useEffect(() => {
    trackDisplayPage({
      config,
      context: reviewFormContext,
      currentPage: CURRENT_PAGE_PERKS_AND_BENEFITS,
      variant: reviewFormContext?.variant ?? '1.0.0',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fields = {
    perksAndBenefitsDescription: review?.perksAndBenefitsDescription,
  };

  const { perksAndBenefitsDescription } = validationRules(config.language);

  const rules = {
    perksAndBenefitsDescription,
  } as Rules<typeof fields>;

  const {
    getTone,
    getMessage,
    handleValueChangeWithValidation,
    validateWithErrors,
  } = useValidation<ReviewPayload>(fields, rules);

  const handleOnContinue = () => {
    const { isValid } = validateWithErrors();

    if (onSubmit) {
      onSubmit();
    }

    trackContinuePressed({
      config,
      context: reviewFormContext,
      validation: isValid ? 'success' : 'mandatory_questions_not_answered',
      currentPage: CURRENT_PAGE_PERKS_AND_BENEFITS,
      variant: reviewFormContext?.variant ?? '1.0.0',
    });

    if (!isValid) {
      return;
    }
  };

  const handleOnSkip = () => {
    trackSkipQuestionPressed({
      config,
      context: reviewFormContext,
      currentPage: CURRENT_PAGE_PERKS_AND_BENEFITS,
      variant: reviewFormContext?.variant ?? '1.0.0',
    });

    setter({
      ...review,
      perksAndBenefitsRating: undefined,
      perksAndBenefitsDescription: undefined,
    });

    setPage(ExperimentTwoProgress.Salary);
  };

  const handleOnChange =
    (updateProperty: keyof ReviewPayload) => (value: Rating) => {
      setter({
        ...review,
        [updateProperty]: value,
      });
    };

  return (
    <ReviewPageLayoutNoStepper
      progress={pageNumber}
      onContinue={handleOnContinue}
      onSkip={handleOnSkip}
      isContinueLoading={isSubmitting}
    >
      <Stack space="large">
        <Heading level="1">Perks and benefits</Heading>
        <Stack space="small">
          <PerksAndBenefitsRatingQuestion
            onChange={handleOnChange('perksAndBenefitsRating')}
            value={review?.perksAndBenefitsRating}
            title="Rate perks and benefits"
            isRequired={true}
          />
          <PerksAndBenefitsDetailsQuestion
            onChange={handleValueChangeWithValidation({
              setter,
              validatorProperty: 'perksAndBenefitsDescription',
              previousValue: review,
            })}
            value={review?.perksAndBenefitsDescription}
            tone={getTone('perksAndBenefitsDescription')}
            message={getMessage('perksAndBenefitsDescription')}
          />
        </Stack>
      </Stack>
    </ReviewPageLayoutNoStepper>
  );
};
